import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'

import {
  dados_status,
  statusBackground,
  statusBorder,
  statusColor,
  statusIcones,
  statusNomes,
  STATUS_COLETADA,
  STATUS_ENVIADO_ANALISE,
} from '.'

/**
 * Regras da linha do tempo de status: labels, paleta e campos por status.
 *
 * Vive aqui (e não no componente) porque é REGRA, não apresentação: o componente
 * só consome. `meta/index.ts` continua sendo a fonte única de nomes, ícones e
 * cores — nada é reescrito neste arquivo.
 */

/** Cods de status usados por nome, para as regras não dependerem de número solto. */
export const STATUS = {
  ABERTA: 1,
  LIBERADA: 2,
  COLETADA: STATUS_COLETADA,
  ENVIADO_ANALISE: STATUS_ENVIADO_ANALISE,
  EM_ANALISE: 5,
  INCONSISTENCIA_MODERADA: 6,
  INCONSISTENCIA_ALTA: 7,
  RESULTADO_COM_ERRO: 8,
  ANALISADA: 9,
} as const

/* -------------------------------------------------------------------------- */
/*                                   PALETA                                   */
/* -------------------------------------------------------------------------- */

export interface PaletaStatus {
  /** Cor do texto e do ícone. */
  cor: string
  /** Fundo do marcador e do chip. */
  fundo: string
  /** Borda do marcador e do chip. */
  borda: string
}

/**
 * Paleta do status. Reusa `statusColor`/`statusBackground`/`statusBorder` de
 * `meta/index.ts`: são as MESMAS cores já em produção na timeline e na tag da
 * tabela — o redesign muda o layout, não a semântica de cor.
 */
export function paletaStatus(cod: number): PaletaStatus {
  return {
    cor: statusColor[cod] ?? 'var(--text-color-light)',
    fundo: statusBackground[cod] ?? 'var(--hover-color)',
    borda: statusBorder[cod] ?? 'var(--border-color)',
  }
}

/** Ícone do status. Reusa o mapa de `meta/index.ts`. */
export function iconeStatus(cod: number): IconDefinition | undefined {
  return statusIcones[cod]
}

/* -------------------------------------------------------------------------- */
/*                                  LABELS                                    */
/* -------------------------------------------------------------------------- */

/**
 * Converte o nome vindo da API/`statusNomes` (CAIXA ALTA) para Sentence case,
 * que é a forma usada na timeline e no chip — assim o usuário liga os dois.
 *
 * Deriva do mapa existente de propósito: duplicar as labels aqui criaria duas
 * fontes de verdade para o mesmo texto.
 */
export function statusLabel(cod: number, nomeDaApi?: string): string {
  const bruto = nomeDaApi ?? statusNomes[cod] ?? ''
  if (!bruto) return ''

  const minusculo = bruto.toLocaleLowerCase('pt-BR')
  return minusculo.charAt(0).toLocaleUpperCase('pt-BR') + minusculo.slice(1)
}

/* -------------------------------------------------------------------------- */
/*                          CAMPOS CONDICIONAIS                               */
/* -------------------------------------------------------------------------- */

/** Única chave de campo condicional existente hoje. */
export type CampoCondicionalNome = 'data_entrega_lab'

export interface CampoCondicional {
  campo: CampoCondicionalNome
  /** Rótulo por extenso e com contexto (não abreviar). */
  label: string
  /**
   * OPCIONAL de propósito: vazia => o campo não é enviado e o valor já gravado
   * no backend é preservado. É o comportamento de produção e não muda aqui.
   */
  obrigatorio: boolean
}

/**
 * Campos que cada status exige para ser aplicado.
 *
 * Reproduz a condicional que existia no componente
 * (`cod === COLETADA || cod === ENVIADO_ANALISE`): a entrega ao laboratório só
 * faz sentido a partir da coleta, e é informada na mesma ação que promove a OS.
 */
export const CAMPOS_POR_STATUS: Record<number, CampoCondicional[]> = {
  [STATUS.COLETADA]: [
    {
      campo: 'data_entrega_lab',
      label: 'Data de entrega ao laboratório',
      obrigatorio: false,
    },
  ],
  [STATUS.ENVIADO_ANALISE]: [
    {
      campo: 'data_entrega_lab',
      label: 'Data de envio ao laboratório',
      obrigatorio: false,
    },
  ],
}

/** Campos exigidos pelo status escolhido (lista vazia = nenhum). */
export function camposDoStatus(cod: number | null): CampoCondicional[] {
  if (!cod) return []
  return CAMPOS_POR_STATUS[cod] ?? []
}

/* -------------------------------------------------------------------------- */
/*                            OPÇÕES DO SELECT                                */
/* -------------------------------------------------------------------------- */

export interface OpcaoStatus {
  cod: number
  /** Sentence case — mesma linguagem visual da timeline. */
  nome: string
}

/**
 * Opções do select, derivadas de `dados_status` (as SELECIONÁVEIS) apenas
 * convertendo a label para Sentence case e respeitando o `hidden` por ambiente.
 *
 * Sem máquina de transições: o backend é quem valida o destino, e restringir
 * aqui removeria ações que hoje existem em produção.
 */
export function opcoesDeStatus(): OpcaoStatus[] {
  return (dados_status ?? [])
    .filter((item) => !item.hidden)
    .map((item) => ({ cod: item.cod, nome: statusLabel(item.cod, item.nome) }))
}

/**
 * O status atual aparece na lista, porém DESABILITADO: selecioná-lo não é uma
 * alteração. `Select` recebe estes cods via `disabledItens`.
 *
 * ANALISADA também fica desabilitado: quem promove a OS para ele é o backend,
 * ao concluir o processamento do arquivo enviado — não o usuário.
 */
export function statusDesabilitados(statusAtual: number | null): number[] {
  const desabilitados: number[] = [STATUS.ANALISADA]

  if (statusAtual && !desabilitados.includes(statusAtual)) {
    desabilitados.push(statusAtual)
  }

  return desabilitados
}
